/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Add from '@mui/icons-material/Add';
import Button from '../components/button';
import Header from '../components/header';
import TableVirtualized from '../components/table-virtualized';
import OnboardingEmpty from '../shared/material_components/onboarding-empty';
import columns from './group-columns';
import {
  useDeleteGroupMutation,
  useGetGroupsQuery,
} from '../services/groups.service';
import { RowDataProps } from '../components/props/tables';
import { emptyGroupsText } from './__fixtures__';
import GroupCreateForm from './group-create-form';
import GroupsEmptyImage from '../images/groups-empty';
import linkTo from '../utils/linkTo';
import pluginNavigation from '../plugins/plugin-navigation';
import type { IGroup as Group } from '../models/group';
import { DeleteConfirmationText } from '../components/__fixtures__/table-helpers';
import { OptionItem } from '../components/cards/card.types';
import ConfirmModal from '../components/modals/confirm-modal';

interface Props {
  readonly onClickGroup: (id: string) => void;
}

export default function GroupsList({ onClickGroup }: Props) {
  const [page, setPage] = useState(1);

  const params = {
    sort: 'name',
    per_page: 100,
    page,
  };

  const { data: group, isLoading, isError } = useGetGroupsQuery(params);
  const [selectedGroup, setSelectedGroup] = useState<Record<any, any> | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [deleteGroup] = useDeleteGroupMutation();
  const mergedGroups = [...groups, ...(group?.groups ?? [])];

  const noGroups = !isLoading && !mergedGroups.length && !isError;
  const hasGroups = !!mergedGroups.length && !isError;

  React.useEffect(() => {
    window.addEventListener('popstate', handleForwardEvent);
    return () => window.removeEventListener('popstate', handleForwardEvent);
  }, []);

  const handleRowAction = React.useCallback(
    (option: OptionItem, item: Record<any, any>) => {
      if (option === OptionItem.Edit) {
        onClickGroup(item.id);
      } else {
        setSelectedGroup(item);
      }
    }, [selectedGroup],
  );

  const handleDeleteGroup = () => {
    deleteGroup(selectedGroup?.id as string);
    setSelectedGroup(null);
  };

  const handleFetchMore = React.useCallback(() => {
    if (group && (page !== group.total_pages)) {
      setPage(page + 1);
      setGroups([...groups, ...group.groups]);
    }
  }, [group]);

  function handleForwardEvent() {
    if (pluginNavigation.entryIdFor(window.location.pathname)) {
      return;
    }

    const id = window.location.pathname.split('/')[2];
    onClickGroup(id);
  }

  return (
    <Stack height="100%" direction="column" spacing={3}>
      <Header
        title="Groups"
        options={[
          <Button
            variant="contained"
            key="create-group"
            size="small"
            fullWidth
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
            startIcon={<Add />}
          >
            Create Group
          </Button>,
        ]}
      />
      <GroupCreateForm anchor={anchor} setAnchor={setAnchor} />
      <ConfirmModal
        open={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
        title="Delete group"
        positive="Delete group"
        onNegative={() => setSelectedGroup(null)}
        onPositive={handleDeleteGroup}
      >
        {DeleteConfirmationText('group')}
      </ConfirmModal>
      {isLoading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
      {!!isError && 'There was an error fetching groups.'}
      {noGroups && (
        <OnboardingEmpty
          Image={GroupsEmptyImage}
          title={"Manage your team's access with groups"}
          description={emptyGroupsText}
          buttonText="LEARN MORE"
          onClick={() => window.open(linkTo.groups.generalHelp, '_blank', 'noopener')}
        />
      )}
      {hasGroups && (
        <TableVirtualized
          rows={mergedGroups}
          columns={columns}
          handleRowClick={({ id }: RowDataProps) => onClickGroup(id as string)}
          onFetchMore={handleFetchMore}
          onRowAction={handleRowAction}
        />
      )}
    </Stack>
  );
}
